function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React from "react";
import { Menu, MenuButton, MenuList, MenuItem, useMenuContext } from "@chakra-ui/menu";
import { useMultiStyleConfig, forwardRef, StylesProvider, useStyles, floral } from "@thebouqs/system";
import { Button } from "@thebouqs/button";
import { __DEV__, runIfFn } from "@thebouqs/utils";
import { CheckIcon } from "@thebouqs/icons";
import { DropdownMenuContext, useDropdownMenuContext } from "./use-dropdownmenu-menu";
/**
 *  Dropdown Menu
 * @example
 * <DropdownMenu>
 *  <DropdownMenuButton>Open</DropdownMenuButton>
 * <DropdownMenuList>
 *  <DropdownMenuItem>Callas</DropdownMenuItem>
 *  <DropdownMenuItem>Lilies</DropdownMenuItem>
 *  <DropdownMenuItem>Roses</DropdownMenuItem>
 * </DropdownMenuList>
 * </DropdownMenu>
 */

export function DropdownMenu(props) {
  var {
    value,
    setValue,
    children
  } = props,
      menuProps = _objectWithoutPropertiesLoose(props, ["value", "setValue", "children"]);

  var styles = useMultiStyleConfig("DropdownMenu", props);
  return /*#__PURE__*/React.createElement(DropdownMenuContext.Provider, {
    value: {
      value,
      setValue
    }
  }, /*#__PURE__*/React.createElement(Menu, menuProps, /*#__PURE__*/React.createElement(StylesProvider, {
    value: styles
  }, children)));
}
DropdownMenu.defaultProps = {
  autoSelect: true,
  isLazy: true,
  matchWidth: true
};

if (__DEV__) {
  DropdownMenu.displayName = "DropdownMenu";
}
/**
 *  Dropdown Menu Button
 * @default colorScheme "green"
 * @default variant "outline"
 */


export var DropdownMenuButton = forwardRef((props, ref) => {
  var _rightIconToggling$sw, _rightIconToggling$sw2, _menuButtonProps$disp;

  var {
    children,
    showSelection,
    fallbackButtonName,
    rightIconToggling,
    iconSpacing
  } = props,
      menuButtonProps = _objectWithoutPropertiesLoose(props, ["children", "showSelection", "fallbackButtonName", "rightIconToggling", "iconSpacing"]);

  var hasIconSwapping = typeof (rightIconToggling == null ? void 0 : rightIconToggling.swap) === "object";
  var animateIcon = rightIconToggling == null ? void 0 : rightIconToggling.animation;
  var openIcon = (rightIconToggling == null ? void 0 : (_rightIconToggling$sw = rightIconToggling.swap) == null ? void 0 : _rightIconToggling$sw.open) || null;
  var closeIcon = (rightIconToggling == null ? void 0 : (_rightIconToggling$sw2 = rightIconToggling.swap) == null ? void 0 : _rightIconToggling$sw2.close) || null;
  var iconMargin = iconSpacing != null ? iconSpacing : "2";
  var {
    value
  } = useDropdownMenuContext();
  var {
    isOpen
  } = useMenuContext();
  var styles = useStyles();

  if (showSelection && !fallbackButtonName) {
    console.warn("[DropdownMenuButton] try to add a `fallbackButtonName`");
  }

  var buttonName = !value && showSelection ? fallbackButtonName : value;
  var display = ((_menuButtonProps$disp = menuButtonProps.display) != null ? _menuButtonProps$disp : animateIcon || hasIconSwapping) ? "inline-flex" : "";
  return /*#__PURE__*/React.createElement(MenuButton, _extends({
    as: Button,
    ref: ref,
    sx: styles.button,
    display: display
  }, menuButtonProps), showSelection ? buttonName : children, animateIcon ? /*#__PURE__*/React.createElement(floral.span, {
    marginLeft: iconMargin
  }, animateIcon(isOpen)) : null, openIcon || closeIcon ? /*#__PURE__*/React.createElement(floral.span, {
    marginLeft: iconMargin
  }, isOpen ? openIcon : closeIcon) : null);
});
DropdownMenuButton.defaultProps = {
  colorScheme: "green",
  variant: "outline"
};

if (__DEV__) {
  DropdownMenuButton.displayName = "DropdownMenuButton";
}

export var DropdownMenuList = forwardRef((props, ref) => {
  var {
    children
  } = props,
      rest = _objectWithoutPropertiesLoose(props, ["rootProps", "children"]);

  return /*#__PURE__*/React.createElement(MenuList, _extends({}, rest, {
    ref: ref
  }), children);
});

if (__DEV__) {
  DropdownMenuList.displayName = "DropdownMenuList";
}

export var DropdownMenuItem = forwardRef((props, ref) => {
  var {
    children,
    item
  } = props,
      itemProps = _objectWithoutPropertiesLoose(props, ["children", "item"]);

  var {
    setValue
  } = useDropdownMenuContext();
  var styles = useStyles();
  return /*#__PURE__*/React.createElement(MenuItem, _extends({
    ref: ref
  }, itemProps, {
    onClick: e => {
      setValue(item != null ? item : children);
      runIfFn(props.onClick, e);
    },
    __css: _extends({}, styles.item)
  }), children);
});

if (__DEV__) {
  DropdownMenuItem.displayName = "DropdownMenuItem";
}
/**
 * Dropdown Menu Check Item
 *
 */

/**
 * Automatically adds a check whenever some item is selected
 */


export var DropdownMenuCheckItem = forwardRef((props, ref) => {
  var {
    children
  } = props,
      itemProps = _objectWithoutPropertiesLoose(props, ["children"]);

  var {
    value
  } = useDropdownMenuContext();
  var isSelected = children === value;
  return /*#__PURE__*/React.createElement(DropdownMenuItem, _extends({
    ref: ref
  }, itemProps, {
    "data-selected": isSelected,
    item: children,
    className: "dropdown-menu-checked-item",
    sx: {
      display: "inline-flex",
      justifyContent: "space-between",
      "&[data-selected='true'] .check": {
        opacity: "0.8"
      },
      "& .check": {
        opacity: "0"
      }
    }
  }), children, /*#__PURE__*/React.createElement(CheckIcon, {
    className: "check"
  }));
});

if (__DEV__) {
  DropdownMenuCheckItem.displayName = "DropdownMenuCheckItem";
}
/**
 * Dropdown Menu Popover Reference
 */

/**
 * Set's itself as the popper reference element, useful when there are multiple dropdown buttons.
 * @example
 * // ...rest of your code
 * const { popoverReferenceProps, menuProps } = useDropdownMenuPopoverReference();
 *
 * return (
 *  <DropdownMenu {...menuProps}>
 *    <DropdownMenuMobileButton />
 *    <DropdownMenuTabletButton />
 *    <DropdownMenuDesktopButton />
 *    {// Every popper will take as reference this dropdown menu and you won't have poppers spawning at the top left of the screen}
 *    <DropdownMenuPopoverReference />
 * </DropdownMenu>
 * )
 */


export var DropdownMenuPopoverReference = floral("div");

if (__DEV__) {
  DropdownMenuPopoverReference.displayName = "DropdownMenuPopoverReference";
}
//# sourceMappingURL=DropdownMenu.js.map